import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { getRecentBookings, deleteBooking, updateFullBooking } from '../../../../api/bookingApi';
import BookingRow from '../Booking/BookingRow';
import BookingModal from '../Booking/BookingModal';
import '../css/generals.css';
import { FaExclamationTriangle } from "react-icons/fa";

const RecentBookingsTable = () => {
  const { t } = useTranslation();
  const [recentBookings, setRecentBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('view'); // 'view', 'edit'
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [bookingToDelete, setBookingToDelete] = useState(null);
  
const fetchRecentBookings = useCallback(async () => {
  try {
    setLoading(true);
    setError(null);
    const response = await getRecentBookings();

    if (Array.isArray(response)) {
      setRecentBookings(response);
    } else if (response && Array.isArray(response.content)) {
      setRecentBookings(response.content);
    } else {
      setRecentBookings([]);
    }
  } catch (err) {
    setError(t('admin.reports.recent.failed_fetch'));
    console.error('Error fetching recent bookings:', err);
    setRecentBookings([]);
  } finally {
    setLoading(false);
  }
}, [t]); 

useEffect(() => {
  fetchRecentBookings();
}, [fetchRecentBookings]);

  // Enhanced handlers for BookingRow actions
  const handleShowDetails = (booking) => {
    setSelectedBooking(booking);
    setModalMode('view');
    setShowModal(true);
  };

  const handleEdit = (booking) => {
    setSelectedBooking(booking);
    setModalMode('edit');
    setShowModal(true);
  };

  const handleDelete = (booking) => {
    setBookingToDelete(booking);
    setShowDeleteConfirm(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedBooking(null);
    setModalMode('view');
  };

  const handleSaveBooking = async (updatedBooking) => {
    try {
      const response = await updateFullBooking(updatedBooking.bookingId, updatedBooking);
      
      // Update the booking in the list
      setRecentBookings(prevBookings =>
        prevBookings.map(booking =>
          booking.bookingId === response.bookingId ? response : booking
        )
      );
      
      handleCloseModal();
      return response;
    } catch (error) {
      console.error('Error updating booking:', error);
      throw error;
    }
  };

  const handleDeleteFromModal = async (booking) => {
    try {
      await deleteBooking(booking.bookingId);
      
      // Remove booking from the list
      setRecentBookings(prevBookings =>
        prevBookings.filter(b => b.bookingId !== booking.bookingId)
      );
      
      handleCloseModal();
    } catch (error) {
      console.error('Error deleting booking:', error);
      throw error;
    }
  };

  const confirmDelete = async () => {
    if (bookingToDelete) {
      try {
        await deleteBooking(bookingToDelete.bookingId);
        
        // Remove booking from the list
        setRecentBookings(prevBookings =>
          prevBookings.filter(booking => booking.bookingId !== bookingToDelete.bookingId)
        );
      } catch (error) {
        console.error('Error deleting booking:', error);
      }
      
      setShowDeleteConfirm(false);
      setBookingToDelete(null);
    }
  };

  const cancelDelete = () => {
    setShowDeleteConfirm(false);
    setBookingToDelete(null);
  };

  if (loading) {
    return (
      <div className="recent-bookings-container">
        <div className="chart-header">
          <h2 className="chart-title">{t('admin.reports.recent.title')}</h2>
          <p className="chart-subtitle">{t('admin.reports.recent.subtitle')}</p>
        </div>
        <div className="loading-state">
          <p>{t('admin.reports.recent.loading')}</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="recent-bookings-container">
        <div className="chart-header">
          <h2 className="chart-title">{t('admin.reports.recent.title')}</h2>
          <p className="chart-subtitle">{t('admin.reports.recent.subtitle')}</p>
        </div>
        <div className="error-state">
          <p className="error-message">{error}</p>
          <button className="retry-btn" onClick={fetchRecentBookings}>
            {t('admin.reports.recent.retry')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="recent-bookings-container">
      <div className="chart-header">
        <h2 className="chart-title">Recent Bookings</h2>
        <p className="chart-subtitle">Latest booking activities</p>
      </div>
      
      <div className="table-container">
        {recentBookings.length > 0 ? (
          <table className="table">
            <thead>
              <tr>
                <th>{t('admin.reports.recent.table_headers.booking_id')}</th>
                <th>{t('admin.reports.recent.table_headers.customer')}</th>
                <th>{t('admin.reports.recent.table_headers.phone')}</th>
                <th>{t('admin.reports.recent.table_headers.booking_date')}</th>
                <th>{t('admin.reports.recent.table_headers.status')}</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {recentBookings.map((booking) => (
                <BookingRow
                  key={booking.bookingId}
                  booking={booking}
                  onShowDetails={handleShowDetails}
                  onEdit={handleEdit}
                  onDelete={handleDelete}
                />
              ))}
            </tbody>
          </table>
        ) : (
          <div className="no-recent-bookings">
            <p>{t('admin.reports.recent.no_bookings')}</p>
          </div>
        )}
      </div>

      {/* Booking Details/Edit Modal */}
      {showModal && selectedBooking && (
        <BookingModal
          booking={selectedBooking}
          mode={modalMode}
          onSave={handleSaveBooking}
          onDelete={handleDeleteFromModal}
          onClose={handleCloseModal}
        />
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && bookingToDelete && (
        <div className="modal-overlay">
          <div className="modal-content delete-confirmation">
            <div className="warning-icon"><FaExclamationTriangle style={{ fontSize: '48px', color: '#dc2626' }}/></div>
            <h3>{t('admin.reports.recent.confirm_delete_title')}</h3>
            <p>
              {t('admin.reports.recent.confirm_delete_msg', {
                id: bookingToDelete.bookingId,
                name: bookingToDelete.customer?.customerName || bookingToDelete.customerName
              })}
            </p>
            <div className="modal-actions">
              <button className="btn-cancel" onClick={cancelDelete}>
                {t('admin.reports.recent.cancel')}
              </button>
              <button className="btn-delete" onClick={confirmDelete}>
                {t('admin.reports.recent.delete')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default RecentBookingsTable;